import React, { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../lp2/Navigation';
import api, { errorMessage, fieldErrors } from '../api';
import ReactGA from 'react-ga4';
import { Button } from '../lp2/ui/button.tsx';
import { User, Mail, Lock, Eye, EyeOff, Users, CheckCircle, AlertCircle, Heart } from 'lucide-react';
import 'react-international-phone/style.css';
import { PhoneInput } from 'react-international-phone';
import { detectUserLocation } from '../lp2/lib/utils.ts';

// Lesbare Bezeichnungen fuer die Fehleruebersicht. Ohne sie stuenden dort
// technische Feldnamen.
const FELD_BEZEICHNUNG = {
    name: 'Ihr Name',
    checkInName: 'Name des Angehörigen',
    email: 'E-Mail-Adresse',
    password: 'Passwort',
    phoneNumber: 'Telefonnummer',
    emergencyContactPhoneNumber: 'Telefonnummer der Kontaktperson',
    consentConfirmed: 'Einverständnis des Angehörigen',
    acceptTerms: 'AGB und Datenschutz',
};

const Register = () => {
    const [searchParams] = useSearchParams();
    // Vorauswahl "Fuer einen Angehoerigen"; ?fuer=selbst schaltet um.
    // Bewusst dasselbe Formular und dieselbe Route – kein zweiter Pfad.
    const [istFuerAngehoerigen, setIstFuerAngehoerigen] = useState(
        searchParams.get('fuer') !== 'selbst'
    );

    const [formData, setFormData] = useState({
        name: '',
        checkInName: '',
        consentConfirmed: false,
        email: '',
        password: '',
        phoneNumber: '',
        emergencyContactPhoneNumber: '',
        preferredChannelEmergencyContact: 'sms',
        preferredChannel: 'sms',
        currency: 'CHF',
        acceptTerms: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [isDetectingLocation, setIsDetectingLocation] = useState(true);
    // Zaehler statt Flag: jeder Fehlversuch soll erneut springen, auch wenn
    // es dieselben Felder sind wie beim letzten Mal.
    const [fehlversuch, setFehlversuch] = useState(0);
    const formRef = useRef(null);
    const navigate = useNavigate();
    // Nur zwei Ereignisse: begonnen und abgeschlossen. ReactGA sendet nichts,
    // solange initialize() nicht lief – das haengt in App.js an der
    // Analyse-Einwilligung.
    const begonnenGemeldet = useRef(false);

    const registrierungBegonnen = () => {
        if (begonnenGemeldet.current) return;
        begonnenGemeldet.current = true;
        ReactGA.event('registrierung_begonnen', {
            modus: istFuerAngehoerigen ? 'angehoeriger' : 'selbst',
        });
    };

    // In Formularreihenfolge, nicht in der Reihenfolge, in der die Pruefungen
    // zufaellig durchlaufen wurden – sonst passt die Liste nicht zu dem, was
    // man beim Scrollen sieht.
    const fehlerListe = useMemo(() => {
        const reihenfolge = Object.keys(FELD_BEZEICHNUNG);
        return Object.entries(errors)
            .filter(([, text]) => Boolean(text))
            .map(([feld, text]) => ({ feld, label: FELD_BEZEICHNUNG[feld] || feld, text }))
            .sort((a, b) => reihenfolge.indexOf(a.feld) - reihenfolge.indexOf(b.feld));
    }, [errors]);

    // Nach einem Fehlversuch zum ersten beanstandeten Feld springen.
    // Ohne das passiert auf dem Handy scheinbar nichts: die Meldungen stehen
    // weit oben am jeweiligen Feld, der Absende-Button aber ganz unten.
    useEffect(() => {
        if (!fehlversuch || !formRef.current) return;

        const meldung = formRef.current.querySelector('[data-error-field]');
        if (!meldung) return;

        meldung.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Fokus auf das zugehoerige Eingabefeld, damit die Tastatur direkt
        // an der richtigen Stelle steht. Schlaegt fehl bei Feldern ohne
        // eigene id (Telefonfelder) – das Springen genuegt dort.
        const feld = meldung.getAttribute('data-error-field');
        const eingabe = formRef.current.querySelector(`#${window.CSS.escape(feld)}`);
        if (eingabe) eingabe.focus({ preventScroll: true });
    }, [fehlversuch]);

    // Auto-detect user location on component mount
    useEffect(() => {
        const autoDetectLocation = async () => {
            try {
                const locationData = await detectUserLocation();
                setFormData(prev => ({
                    ...prev,
                    country: locationData.country,
                    currency: locationData.currency
                }));
            } catch (error) {
                console.log('Location detection failed, using defaults:', error);
            } finally {
                setIsDetectingLocation(false);
            }
        };

        autoDetectLocation();
    }, []);

    const validateForm = () => {
        let formIsValid = true;
        let newErrors = {};

        // Name des Kontoinhabers. Fehlte bisher clientseitig – der Server
        // lehnt ihn ab, im Formular blieb das Feld unbeanstandet.
        if (formData.name.trim().length < 2) {
            formIsValid = false;
            newErrors.name = istFuerAngehoerigen
                ? 'Bitte geben Sie Ihren Namen an.'
                : 'Bitte geben Sie Ihren Vor- und Nachnamen an.';
        }

        // Email validation
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
            formIsValid = false;
            newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
        }

        // Phone number validation (require country code)
        if (!/^\+\d{1,3}\d{4,14}(?:x.+)?$/i.test(formData.phoneNumber)) {
            formIsValid = false;
            newErrors.phoneNumber = 'Bitte geben Sie eine gültige Telefonnummer mit Ländervorwahl ein.';
        }

        // Emergency contact phone number validation (require country code)
        if (!/^\+\d{1,3}\d{4,14}(?:x.+)?$/i.test(formData.emergencyContactPhoneNumber)) {
            formIsValid = false;
            newErrors.emergencyContactPhoneNumber = 'Bitte geben Sie eine gültige Telefonnummer für den Notfallkontakt mit Ländervorwahl ein.';
        }

        // Angehoerigen-Modus: Name der betreuten Person und Einwilligung.
        if (istFuerAngehoerigen) {
            if (formData.checkInName.trim().length < 2) {
                formIsValid = false;
                newErrors.checkInName = 'Bitte geben Sie den Namen Ihres Angehörigen an.';
            }
            if (!formData.consentConfirmed) {
                formIsValid = false;
                newErrors.consentConfirmed =
                    'Ohne das Einverständnis Ihres Angehörigen können wir keine Nachrichten senden.';
            }
        }

        // Der Notfallkontakt muss eine andere Person erreichen.
        const digits = (v) => String(v || '').replace(/\D/g, '');
        if (formData.phoneNumber && formData.emergencyContactPhoneNumber
            && digits(formData.phoneNumber) === digits(formData.emergencyContactPhoneNumber)) {
            formIsValid = false;
            newErrors.emergencyContactPhoneNumber =
                'Der Notfallkontakt muss sich von Ihrer eigenen Nummer unterscheiden.';
        }

        // Password validation
        if (formData.password.length < 8) {
            formIsValid = false;
            newErrors.password = 'Das Passwort muss mindestens 8 Zeichen lang sein.';
        }

        // Terms and conditions validation
        if (!formData.acceptTerms) {
            formIsValid = false;
            newErrors.acceptTerms = 'Sie müssen den Allgemeinen Geschäftsbedingungen und den Datenschutzbestimmungen zustimmen.';
        }

        setErrors(newErrors);
        return formIsValid;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        // Clear error when user starts typing
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: '' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!validateForm()) {
            setLoading(false);
            setFehlversuch((n) => n + 1);
            return;
        }

        try {
            // Nur die Felder senden, die der Server kennt – acceptTerms
            // gehoert nicht in den Request. Kanal und Land sind nicht mehr im
            // Formular: Land wird erkannt, Kanal ist SMS und im Profil aenderbar.
            const response = await api.post('/register', {
                name: formData.name,
                email: formData.email,
                password: formData.password,
                phoneNumber: formData.phoneNumber,
                emergencyContactPhoneNumber: formData.emergencyContactPhoneNumber,
                preferredChannel: formData.preferredChannel,
                preferredChannelEmergencyContact: formData.preferredChannelEmergencyContact,
                country: formData.country,
                // Der Modus ergibt sich serverseitig allein aus checkInName.
                checkInName: istFuerAngehoerigen ? formData.checkInName.trim() : null,
                consentConfirmed: istFuerAngehoerigen ? formData.consentConfirmed : undefined,
            });

            ReactGA.event('registrierung_abgeschlossen', {
                modus: istFuerAngehoerigen ? 'angehoeriger' : 'selbst',
            });

            localStorage.setItem('token', response.data.token);
            // Kein Zwangswechsel auf die Zahlung mehr – zuerst die Gewissheit,
            // dass der Dienst laeuft. Zahlungsdaten sind dort ein Angebot.
            navigate('/willkommen', {
                state: {
                    name: formData.name,
                    checkInName: istFuerAngehoerigen ? formData.checkInName.trim() : null,
                    trialEndsAt: response.data.trialEndsAt,
                },
            });
        } catch (error) {
            if (error.response?.status === 400
                && error.response.data?.message === 'User already exists') {
                setError('Ein Benutzer mit dieser E-Mail-Adresse existiert bereits.');
            } else {
                // Feldbezogene Serverfehler direkt am jeweiligen Feld anzeigen.
                const serverFieldErrors = fieldErrors(error);
                if (Object.keys(serverFieldErrors).length > 0) {
                    setErrors(serverFieldErrors);
                    setError('Bitte prüfen Sie die markierten Felder.');
                    setFehlversuch((n) => n + 1);
                } else {
                    console.error('Registrierung fehlgeschlagen:', error);
                    setError(errorMessage(error, 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.'));
                }
            }
            setLoading(false);
        }
    };

    return (
        <Fragment>
            <style>
                {`
                    .react-international-phone-input {
                        border: 1px solid #d1d5db;
                        border-radius: 8px;
                        padding: 12px;
                        transition: all 0.2s;
                    }
                    .react-international-phone-input:focus-within {
                        border-color: #3b82f6;
                        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
                    }
                    .react-international-phone-input.error {
                        border-color: #ef4444;
                    }
                    .react-international-phone-input .react-international-phone-country-selector {
                        margin-right: 8px;
                    }
                    .react-international-phone-input .react-international-phone-input-field {
                        margin-left: 8px;
                    }
                `}
            </style>
            <Navigation />
            <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                            <User className="h-6 w-6 text-blue-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                        Neu registrieren
                        </h2>
                        <p className="text-gray-600">
                            Erstellen Sie Ihr Konto für "Es geht mir gut"
                        </p>
                    </div>

                    {/* Error Alert */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
                            <span className="text-red-800">{error}</span>
                        </div>
                    )}

                    {/* Registration Form */}
                    <div className="bg-white rounded-xl shadow-lg p-8">
                        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" noValidate
                              onFocusCapture={registrierungBegonnen}>
                            {/* Modus-Auswahl */}
                            <div>
                                <span className="block text-sm font-medium text-gray-700 mb-3">
                                    Für wen richten Sie den Dienst ein?
                                </span>
                                <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Für wen richten Sie den Dienst ein?">
                                    {[
                                        { wert: true, titel: 'Für einen Angehörigen', text: 'Ihre Mutter, Ihr Vater oder eine andere Person, die allein lebt', Icon: Heart },
                                        { wert: false, titel: 'Für mich selbst', text: 'Sie erhalten die täglichen Nachrichten selbst', Icon: User },
                                    ].map(({ wert, titel, text, Icon }) => (
                                        <button
                                            key={String(wert)}
                                            type="button"
                                            role="radio"
                                            aria-checked={istFuerAngehoerigen === wert}
                                            onClick={() => setIstFuerAngehoerigen(wert)}
                                            className={`text-left p-4 rounded-lg border-2 transition-colors ${
                                                istFuerAngehoerigen === wert
                                                    ? 'border-blue-600 bg-blue-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <span className="flex items-center font-medium text-gray-900 mb-1">
                                                <Icon className={`h-5 w-5 mr-2 ${istFuerAngehoerigen === wert ? 'text-blue-600' : 'text-gray-400'}`} />
                                                {titel}
                                            </span>
                                            <span className="block text-sm text-gray-600">{text}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Personal Information Section */}
                            <div className="border-t pt-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <User className="h-5 w-5 text-blue-600 mr-2" />
                                    {istFuerAngehoerigen ? 'Ihre Daten' : 'Persönliche Daten'}
                                </h3>
                                
                                <div className="space-y-4">
                                    {/* Name Field */}
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                            {istFuerAngehoerigen ? 'Ihr Name' : 'Vorname und Name'}
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                                    errors.name ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="Ihr vollständiger Name"
                                                required
                                            />
                                        </div>
                                        {errors.name && (
                                            <p data-error-field="name" className="mt-1 text-sm text-red-600">{errors.name}</p>
                                        )}
                                    </div>

                                    {/* Name des Angehoerigen – nur im Angehoerigen-Modus */}
                                    {istFuerAngehoerigen && (
                                        <div>
                                            <label htmlFor="checkInName" className="block text-sm font-medium text-gray-700 mb-2">
                                                Name des Angehörigen
                                            </label>
                                            <input
                                                type="text"
                                                id="checkInName"
                                                name="checkInName"
                                                value={formData.checkInName}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                                    errors.checkInName ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="z.B. Maria Muster"
                                            />
                                            <p className="mt-1 text-sm text-gray-500">
                                                Mit diesem Namen sprechen wir Ihren Angehörigen in den Nachrichten an.
                                            </p>
                                            {errors.checkInName && (
                                                <p data-error-field="checkInName" className="mt-1 text-sm text-red-600">{errors.checkInName}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Email Field */}
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                            E-Mail-Adresse
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                                    errors.email ? 'border-red-300' : 'border-gray-300'
                                                }`}
                                                placeholder="ihre.email@beispiel.ch"
                                                required
                                            />
                                        </div>
                                        {errors.email && (
                                            <p data-error-field="email" className="mt-1 text-sm text-red-600">{errors.email}</p>
                                        )}
                                    </div>

                                    {/* Password Fields */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                                Passwort
                                            </label>
                                            <div className="relative">
                                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    id="password"
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                                        errors.password ? 'border-red-300' : 'border-gray-300'
                                                    }`}
                                                    placeholder="Mindestens 6 Zeichen"
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                >
                                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                                </button>
                                            </div>
                                            {errors.password && (
                                                <p data-error-field="password" className="mt-1 text-sm text-red-600">{errors.password}</p>
                                            )}
                                        </div>

                                        
                                    </div>

                                    {/* Phone Number Field */}
                                    <div>
                                        <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                            {istFuerAngehoerigen
                                                ? `Handynummer${formData.checkInName.trim() ? ' von ' + formData.checkInName.trim() : ' des Angehörigen'}`
                                                : 'Ihre Telefonnummer'}
                                        </label>
                                        <div className="relative">
                                            {!isDetectingLocation && (
                                                <PhoneInput
                                                    key={`phone-${formData.country}`}
                                                    defaultCountry={formData.country.toLowerCase()}
                                                    value={formData.phoneNumber}
                                                    onChange={(phone) => handleChange({ target: { name: 'phoneNumber', value: phone } })}
                                                    preferredCountries={['at', 'de', 'ch']}
                                                    className={`react-international-phone-input ${
                                                        errors.phoneNumber ? 'error' : ''
                                                    }`}
                                                    placeholder="Telefonnummer mit Vorwahl"
                                                    required
                                                    inputStyle={{
                                                        border: 'none',
                                                        outline: 'none',
                                                        fontSize: '16px',
                                                        padding: '0',
                                                        backgroundColor: 'transparent'
                                                    }}
                                                    containerStyle={{
                                                        width: '100%'
                                                    }}
                                                />
                                            )}
                                            {isDetectingLocation && (
                                                <div className="w-full py-3 px-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                                                    Standort wird erkannt...
                                                </div>
                                            )}
                                        </div>
                                        {errors.phoneNumber && (
                                            <p data-error-field="phoneNumber" className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
                                        )}
                                        <p className="mt-1 text-sm text-gray-500">
                                            Bitte geben Sie eine gültige Telefonnummer mit Ländervorwahl ein.
                                        </p>
                                    </div>

                                    {/* Preferred Channel */}
                                    {istFuerAngehoerigen && (
                                        <p className="text-sm text-gray-500 -mt-2">
                                            An diese Nummer geht jeden Morgen die kurze Nachricht.
                                        </p>
                                    )}

                                    

                                    {/* Country Selection */}
                                    

                                </div>
                            </div>

                            {/* Emergency Contact Section */}
                            <div className="border-t pt-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <Users className="h-5 w-5 text-blue-600 mr-2" />
                                    {istFuerAngehoerigen ? 'Wer wird benachrichtigt?' : 'Notfallkontakt'}
                                </h3>
                                
                                <div className="space-y-4">
                                    {/* Emergency Contact Phone */}
                                    <div>
                                        <label htmlFor="emergencyContactPhoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                            {istFuerAngehoerigen ? 'Ihre Handynummer' : 'Telefonnummer der Kontaktperson'}
                                        </label>
                                        {istFuerAngehoerigen && (
                                            <p className="text-sm text-gray-500 mb-2">
                                                Hierhin melden wir uns, wenn keine Antwort kommt. Sonst hören Sie nichts von uns.
                                            </p>
                                        )}
                                        <div className="relative">
                                            {!isDetectingLocation && (
                                                <PhoneInput
                                                    key={`emergency-phone-${formData.country}`}
                                                    defaultCountry={formData.country.toLowerCase()}
                                                    value={formData.emergencyContactPhoneNumber}
                                                    onChange={(phone) => handleChange({ target: { name: 'emergencyContactPhoneNumber', value: phone } })}
                                                    preferredCountries={['at', 'de', 'ch']}
                                                    className={`react-international-phone-input ${
                                                        errors.emergencyContactPhoneNumber ? 'error' : ''
                                                    }`}
                                                    placeholder="Telefonnummer mit Vorwahl"
                                                    required
                                                    inputStyle={{
                                                        border: 'none',
                                                        outline: 'none',
                                                        fontSize: '16px',
                                                        padding: '0',
                                                        backgroundColor: 'transparent'
                                                    }}
                                                    containerStyle={{
                                                        width: '100%'
                                                    }}
                                                />
                                            )}
                                            {isDetectingLocation && (
                                                <div className="w-full py-3 px-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                                                    Standort wird erkannt...
                                                </div>
                                            )}
                                        </div>
                                        {errors.emergencyContactPhoneNumber && (
                                            <p data-error-field="emergencyContactPhoneNumber" className="mt-1 text-sm text-red-600">{errors.emergencyContactPhoneNumber}</p>
                                        )}
                                        <p className="mt-1 text-sm text-gray-500">
                                            Bitte geben Sie eine Telefonnummer mit Ländervorwahl einer Kontaktperson ein.
                                        </p>
                                    </div>

                                    {/* Emergency Contact Preferred Channel */}
                                    
                                </div>
                            </div>

                            {/* Terms and Conditions */}
                            <div className="border-t pt-6">
                                {/* Einwilligung der betreuten Person – bei Nachrichten an Dritte Pflicht */}
                                {istFuerAngehoerigen && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                        <div className="flex items-start">
                                            <input
                                                type="checkbox"
                                                id="consentConfirmed"
                                                name="consentConfirmed"
                                                checked={formData.consentConfirmed}
                                                onChange={(e) => handleChange({ target: { name: 'consentConfirmed', value: e.target.checked } })}
                                                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <label htmlFor="consentConfirmed" className="ml-3 text-sm text-gray-800">
                                                Ich habe {formData.checkInName.trim() || 'meinen Angehörigen'} gefragt und er/sie ist
                                                einverstanden, diese täglichen Nachrichten zu erhalten.
                                            </label>
                                        </div>
                                        {errors.consentConfirmed && (
                                            <p data-error-field="consentConfirmed" className="mt-2 text-sm text-red-600">{errors.consentConfirmed}</p>
                                        )}
                                    </div>
                                )}

                                <div className="flex items-start space-x-3">
                                    <input
                                        type="checkbox"
                                        id="acceptTerms"
                                        name="acceptTerms"
                                        checked={formData.acceptTerms}
                                        onChange={(e) => handleChange({ target: { name: 'acceptTerms', value: e.target.checked } })}
                                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        required
                                    />
                                    <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                                        Ich akzeptiere die{' '}
                                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                                            Datenschutzbestimmungen
                                        </a>{' '}
                                        und die{' '}
                                        <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                                            AGB
                                        </a>.
                                    </label>
                                </div>
                                {errors.acceptTerms && (
                                    <p data-error-field="acceptTerms" className="mt-1 text-sm text-red-600">{errors.acceptTerms}</p>
                                )}
                            </div>

                            {/* Fehleruebersicht unmittelbar ueber dem Button.
                                Die Meldungen an den Feldern allein genuegen nicht:
                                auf dem Handy steht man beim Absenden ganz unten und
                                saehe oben am Feld nichts von einem Fehler. */}
                            {(fehlerListe.length > 0 || error) && (
                                <div
                                    role="alert"
                                    aria-live="assertive"
                                    className="bg-red-50 border border-red-200 rounded-lg p-4"
                                >
                                    <div className="flex items-start">
                                        <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-red-800">
                                                {fehlerListe.length > 0
                                                    ? (fehlerListe.length === 1
                                                        ? 'Eine Angabe fehlt oder ist nicht korrekt:'
                                                        : `${fehlerListe.length} Angaben fehlen oder sind nicht korrekt:`)
                                                    : error}
                                            </p>
                                            {fehlerListe.length > 0 && (
                                                <ul className="mt-2 space-y-1">
                                                    {fehlerListe.map(({ feld, label, text }) => (
                                                        <li key={feld} className="text-sm text-red-700">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const ziel = formRef.current?.querySelector(
                                                                        `[data-error-field="${feld}"]`
                                                                    );
                                                                    ziel?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                                    formRef.current
                                                                        ?.querySelector(`#${window.CSS.escape(feld)}`)
                                                                        ?.focus({ preventScroll: true });
                                                                }}
                                                                className="text-left underline hover:no-underline"
                                                            >
                                                                <span className="font-medium">{label}:</span> {text}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Submit Button */}
                            <div className="pt-4">
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                >
                                    {loading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Registriere...
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center">
                                            <CheckCircle className="h-5 w-5 mr-2" />
                                            Registrieren
                                        </div>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>

                    {/* Login Link */}
                    <div className="text-center mt-6">
                        <p className="text-gray-600">
                            Bereits ein Konto?{' '}
                            <a href="/login" className="text-blue-600 hover:text-blue-800 font-semibold">
                                Hier anmelden
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default Register;
